import { useState } from "react";
import { Link } from "react-router-dom";

import { absoluteTime, relativeTime } from "../../lib/time";
import { crmApi } from "../../lib/crm-api";
import type {
  TimelineAttachment,
  TimelineEntry,
  TimelineKind,
} from "../../lib/crm-types";
import { cn } from "../ui/cn";
import { formatBytes } from "./attachment-format";
import { toHtml, toText } from "./note-html";

/**
 * One row of a timeline, and the vocabulary that goes with it.
 *
 * Shared by the CRM's company page and the Helpdesk's activity drawer rather
 * than copied into both: they render the same objects, and a drifted copy would
 * mean a call reads one way on the company page and another way over a ticket.
 * Nobody would report that as a bug -- they would just trust one of the two
 * less.
 */

/**
 * The filter chips.
 *
 * Grouped rather than one per type: nobody thinks "show me lifecycle_changed",
 * they think "show me the conversations" or "just the calls".
 */
export const TIMELINE_FILTERS: {
  id: string;
  label: string;
  kinds: TimelineKind[];
}[] = [
  { id: "all", label: "Everything", kinds: [] },
  /* Both kinds of ticket. Somebody asking "what has gone wrong here" does not
     care which system it was handled in. */
  { id: "tickets", label: "Tickets", kinds: ["ticket", "external_ticket"] },
  { id: "notes", label: "Notes", kinds: ["note"] },
  { id: "calls", label: "Calls", kinds: ["call"] },
  { id: "messages", label: "Messages", kinds: ["whatsapp", "email"] },
  { id: "meetings", label: "Meetings", kinds: ["meeting"] },
  { id: "commercial", label: "Quotes & orders", kinds: ["quote", "order"] },
  { id: "tasks", label: "Tasks", kinds: ["task"] },
  {
    id: "changes",
    label: "Changes",
    kinds: [
      "company_created",
      "owner_changed",
      "lifecycle_changed",
      "contact_added",
      "merged",
      /* Filed with the changes rather than given a filter of its own: it is an
         audit entry like the rest, and a "Credit checks" tab would be one tab
         per rarely-asked question. */
      "credit_checked",
      "archived",
      "restored",
    ],
  },
];

/** Only the things a person typed. The audit entries are the record. */
const EDITABLE = new Set<TimelineKind>([
  "note",
  "call",
  "meeting",
  "whatsapp",
  "email",
  "quote",
  "order",
  /* The logged one only. A real ticket is a live row somebody would be deleting
     out from under the Helpdesk, and this is not the screen for that. */
  "external_ticket",
]);

/**
 * How long the author has to correct what they typed.
 *
 * Mirrors `edit-window.ts` on the server, which is the one that decides. This
 * copy exists only so the button is not offered on something the server would
 * refuse -- if the two ever disagree, the server wins and the reader sees an
 * error, which is the right way round.
 */
export const EDIT_WINDOW_MINUTES = 30;

/** Whether to offer this reader an Edit button on this entry. */
export function canStillEdit(
  entry: TimelineEntry,
  userId: number | null,
  now: number,
): boolean {
  if (userId === null) return false;
  // Merged-in tickets have no createdAt and are not ours to edit.
  if (entry.createdAt === null) return false;
  if (entry.actorId !== userId) return false;
  if (!EDITABLE.has(entry.kind)) return false;

  const recorded = new Date(entry.createdAt).getTime();
  return now <= recorded + EDIT_WINDOW_MINUTES * 60_000;
}

const TONE: Record<string, string> = {
  ticket: "bg-brand-100 text-brand-700 dark:bg-brand-950 dark:text-brand-300",
  /* Dimmer than a real ticket, and the same family: related, not the same
     thing. */
  external_ticket:
    "bg-brand-50 text-brand-600 dark:bg-brand-950/50 dark:text-brand-400",
  note: "bg-surface-2 text-muted-on-tint",
  call: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  /* WhatsApp shares the call's green: both are the customer talking to a
     person, and the badge text is what says which. */
  whatsapp:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  email: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  meeting: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  task: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  quote: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  order: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  /* The only audit entry with a colour of its own. It is the one that cost
     money, and it should be findable by eye in a two-year timeline. */
  credit_checked:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300",
};

const LABELS: Record<string, string> = {
  ticket: "Ticket",
  /* Named for where it was handled, because the badge sits next to real
     tickets and "Ticket" twice would say they are the same thing. */
  external_ticket: "Elsewhere",
  note: "Note",
  call: "Call",
  whatsapp: "WhatsApp",
  email: "Email",
  meeting: "Meeting",
  task: "Task",
  quote: "Quote",
  order: "Order",
  company_created: "Created",
  /* Not "Account manager": the row's own line already says the account manager
     changed and who to, so the badge was saying it twice -- and it was the
     longest label by half again, which set the width of the badge column for
     every other row in the timeline. */
  owner_changed: "Updated",
  lifecycle_changed: "Stage",
  contact_added: "Contact",
  merged: "Merged",
  credit_checked: "Credit",
  archived: "Archived",
  restored: "Restored",
};

export function TimelineEntryRow({
  entry,
  onEdit,
  onOpenTicket,
}: {
  entry: TimelineEntry;
  /**
   * How to save a correction, or undefined when this entry is not the reader's
   * to correct -- somebody else wrote it, or the window has shut. Undefined is
   * what hides the button, so the page never offers an edit the server would
   * refuse.
   */
  onEdit?: (patch: { subject?: string | null; body?: string | null }) => void;
  /**
   * How to open a ticket, when the caller is already somewhere a ticket can be
   * shown. The CRM leaves this unset and gets a router link into the Helpdesk;
   * the drawer sets it, because it is floating over the ticket list and can
   * select one in place rather than navigating the page out from under itself.
   */
  onOpenTicket?: (ticketId: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const data = entry.data ?? {};
  const taskUrl = typeof data.url === "string" ? data.url : null;
  const taskDone = typeof data.completedAt === "string";
  /*
   * Who owns it. Worth a word here because a one-click task never showed
   * anybody a picker -- this row is where the person who pressed the button
   * finds out it landed on them, or on the account's manager.
   */
  const owners = Array.isArray(data.assigneeNames)
    ? data.assigneeNames.filter((name): name is string => typeof name === "string")
    : [];

  return (
    /*
     * Three columns -- badge, entry, time -- taken from the list's own grid
     * rather than declared here; see the `<ol>` in `Timeline.tsx`. Subgrid is
     * what makes every badge the same width: the columns are sized once, to
     * the widest badge and the widest timestamp on screen, and every row is
     * measured against them. The titles start on one line instead of stepping
     * in and out with the length of the word beside them, and the times end on
     * one line instead of drifting with the title above them.
     *
     * A fixed badge width would do the same and was the first attempt, but it
     * has to be sized for the longest label whether or not that label is on
     * screen, so every row pays an indent for a kind that may appear on none
     * of them. This way a view filtered to nothing but tasks is as narrow as
     * it can be.
     *
     * The row is two grid rows, not one, and that is what keeps the badge and
     * the time beside the words they belong to: they sit in the first row with
     * the title and its details, so `self-center` centres them on that pair of
     * lines. Everything that can be tall -- a note's body, a wall of photos --
     * is in the second row. Centring against the whole entry instead left the
     * badge floating halfway down a 112px thumbnail, pointing at nothing.
     */
    <li className="group col-span-3 grid grid-cols-subgrid">
      {/* The badge is the grid item itself, so it stretches to the column and
          needs no width of its own. */}
      <span
        className={cn(
          "self-center rounded px-1.5 py-0.5 text-center text-[10px] font-medium uppercase tracking-wide",
          TONE[entry.kind] ?? "bg-surface-2 text-faint",
        )}
      >
        {LABELS[entry.kind] ?? entry.kind}
      </span>

      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          {/* Two lines on a phone before it gives up, one on a desktop. The
              badge and the time take a third of a phone's width between them,
              and cut to one line there "Moved Tom Ellery to this comp…" was
              every contact entry -- the half that said what happened was the
              half that went. */}
          <span className="min-w-0 flex-1 text-sm font-medium text-content max-md:line-clamp-2 md:truncate">
            {entry.kind === "ticket" && entry.ticketId ? (
              onOpenTicket ? (
                <button
                  type="button"
                  onClick={() => onOpenTicket(entry.ticketId!)}
                  className="text-left hover:text-brand-600 max-md:line-clamp-2 md:truncate"
                >
                  {entry.subject ?? `Ticket #${entry.ticketId}`}
                </button>
              ) : (
                /* Straight into the Helpdesk. The ticket is the conversation;
                   the CRM only ever shows that it happened. */
                <Link
                  to={`/v/open/${entry.ticketId}`}
                  className="hover:text-brand-600"
                >
                  {entry.subject ?? `Ticket #${entry.ticketId}`}
                </Link>
              )
            ) : taskUrl ? (
              /*
                Coloured and underlined rather than plain-until-hovered, which
                is what the rest of this row is. Everything else in the
                timeline happened in Pulse; this one line leaves for another
                application, and a link that only announces itself on hover is
                one nobody on a touchscreen ever discovers. The arrow says it
                opens elsewhere before it is clicked.
              */
              <a
                href={taskUrl}
                target="_blank"
                rel="noreferrer"
                title="Open this task in Lane"
                className={cn(
                  "text-brand-600 underline decoration-brand-300 underline-offset-2",
                  "hover:text-brand-700 hover:decoration-brand-600",
                  taskDone && "line-through",
                )}
              >
                {entry.subject}
                <span aria-hidden className="ml-1 text-[10px] no-underline">
                  ↗
                </span>
              </a>
            ) : (
              (entry.subject ?? <span className="text-muted">Note</span>)
            )}
          </span>

          {/* Revealed on hover, so a dense timeline is not a wall of
              controls -- and it disappears on its own when the window shuts,
              which is the part the reader has to be able to trust.

              A touchscreen has no hover, so there Edit is simply shown: it
              only exists for a few minutes after writing, which is exactly
              when somebody on a phone spots the typo.

              There is no Delete beside it any more. The timeline is the
              account's history, and a press that asked nothing was one slip
              from losing a note somebody else needed; a mistake is fixed with
              Edit while it is fresh. Integrations still remove what they
              pushed, over `/v1`, which is how a corrected order stops counting
              towards spend.

              It stays in this column, beside the title, rather than out with
              the time. In the time's column they would be measured into its
              width, and a column sized by something only a hover reveals is a
              column that jumps -- every row in the list shifting sideways as
              the pointer crosses one of them. */}
          {onEdit && !editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              title="Fix what you wrote. Only for a short while after writing it."
              className="hidden shrink-0 text-[11px] text-faint hover:text-brand-600 group-hover:block pointer-coarse:block"
            >
              Edit
            </button>
          ) : null}
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-faint">
          {/* Only set when the view spans a group, and then it is the first
              thing worth knowing about the row. */}
          {entry.companyName ? (
            <Link
              to={`/crm/companies/${entry.companyId}`}
              className="text-muted hover:text-brand-600"
            >
              {entry.companyName}
            </Link>
          ) : null}
          {entry.actorName ? <span>{entry.actorName}</span> : null}
          {entry.contactName ? <span>with {entry.contactName}</span> : null}
          {entry.direction ? <span>{entry.direction}</span> : null}
          {typeof data.durationMinutes === "number" ? (
            <span>{data.durationMinutes} min</span>
          ) : null}
          {typeof data.location === "string" && data.location ? (
            <span>{data.location}</span>
          ) : null}
          {typeof data.outcome === "string" ? (
            <span
              className={cn(
                "rounded px-1 uppercase",
                data.outcome === "lost"
                  ? "text-sla-breached"
                  : data.outcome === "won"
                    ? "text-sla-ok"
                    : "",
              )}
            >
              {/* Underscores out: a quote's outcome is one word, but a call's
                  comes from the phone system as `no_answer`, and the chip is
                  read rather than parsed. */}
              {data.outcome.replace(/_/g, " ")}
            </span>
          ) : null}
          {/* What the quote or order was worth. Net of VAT, like every other
              money figure the CRM shows, and said so -- a bare pound sign on a
              trade account is ambiguous by about twenty per cent. */}
          {typeof data.netTotal === "number" ? (
            <span>
              £
              {data.netTotal.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}{" "}
              net
            </span>
          ) : null}
          {/* Their case number, when whoever logged it had it to hand. */}
          {entry.kind === "external_ticket" &&
          typeof data.reference === "string" &&
          data.reference ? (
            <span>ref {data.reference}</span>
          ) : null}
          {entry.ticketStatus ? <span>{entry.ticketStatus}</span> : null}
          {entry.kind === "task" && owners.length > 0 ? (
            <span>for {owners.join(", ")}</span>
          ) : null}
          {/* "open in Lane" read as an instruction and did nothing when
              clicked, which is the same complaint as the title above: make
              the words that name the other application the way into it.

              Coloured by state, in the same two tokens the outcome above uses
              -- amber while it is somebody's problem, green once it is not.
              Scanning a company's history for what is still outstanding was
              reading every row's last word; now it is a glance. The open state
              keeps the arrow and the underline on hover, because the colour
              says "not finished" and nothing else here would say "and this is
              the way to it". */}
          {entry.kind === "task" ? (
            taskDone ? (
              <span className="font-medium text-sla-ok">done</span>
            ) : taskUrl ? (
              <a
                href={taskUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-sla-warning hover:underline"
              >
                open in Lane ↗
              </a>
            ) : (
              <span className="font-medium text-sla-warning">open in Lane</span>
            )
          ) : null}
          {/* Said plainly. The timeline is evidence, and a reader has to be
              able to tell that what they are reading is not quite what was
              first written. */}
          {entry.editedAt ? (
            <span title={`Edited ${absoluteTime(entry.editedAt)}`}>edited</span>
          ) : null}
        </div>
      </div>

      <time
        title={absoluteTime(entry.occurredAt)}
        className="self-center whitespace-nowrap text-[11px] text-faint"
      >
        {relativeTime(entry.occurredAt)}
      </time>

      {/*
        The second row: everything that can run to any height, and the rule
        that closes the entry off. Always rendered, even with nothing in it --
        it is what draws the line, and an entry with no body still needs one.
        It starts at the second column so a body sits under its own title
        rather than under the badge.
      */}
      <div className="col-span-2 col-start-2 min-w-0 border-b border-line pb-3">
        {editing && onEdit ? (
          <EditForm
            entry={entry}
            onCancel={() => setEditing(false)}
            onSave={(patch) => {
              onEdit(patch);
              setEditing(false);
            }}
          />
        ) : entry.body ? (
          /*
           * Note bodies are HTML that the API sanitised on write -- the same
           * treatment canned responses get, because a note can be quoted into
           * an email where our sanitiser never runs again.
           */
          <div
            className="email-body mt-1 text-muted"
            dangerouslySetInnerHTML={{ __html: entry.body }}
          />
        ) : null}

        {entry.attachments.length > 0 ? (
          <AttachmentList files={entry.attachments} />
        ) : null}
      </div>
    </li>
  );
}

/**
 * The files on one entry: pictures shown, everything else listed.
 *
 * Showing images rather than linking them is the whole point of the feature --
 * a photo of a damaged pallet is evidence somebody should see while scanning
 * the timeline, not a download they have to decide is worth opening.
 *
 * `isImage` is the server's answer, not a guess from the filename here. It is
 * true only for the raster types the server will actually serve inline, so a
 * file this renders as a picture is one the browser is allowed to draw -- an
 * SVG, which can carry script, comes back false and lands in the list instead.
 */
function AttachmentList({ files }: { files: TimelineAttachment[] }) {
  const images = files.filter((file) => file.isImage);
  const rest = files.filter((file) => !file.isImage);

  return (
    <div className="mt-2 space-y-1.5">
      {images.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {images.map((file) => (
            <a
              key={file.id}
              href={crmApi.attachmentUrl(file.id)}
              target="_blank"
              rel="noreferrer"
              title={`${file.filename} — ${formatBytes(file.sizeBytes)}`}
              className="block overflow-hidden rounded-md border border-line hover:border-brand-400"
            >
              <img
                src={crmApi.attachmentUrl(file.id)}
                alt={file.filename}
                loading="lazy"
                className="h-28 w-28 bg-app object-cover"
              />
            </a>
          ))}
        </div>
      ) : null}

      {rest.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {rest.map((file) => (
            <li key={file.id}>
              <a
                href={crmApi.attachmentUrl(file.id, { download: true })}
                className="flex items-center gap-1.5 rounded-md border border-line bg-app px-2 py-1 text-xs text-muted hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400"
              >
                <span className="max-w-[16rem] truncate">{file.filename}</span>
                <span className="text-faint">{formatBytes(file.sizeBytes)}</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Fixing what you wrote, in place.
 *
 * In the row rather than in a dialog, and rather than reopening the composer:
 * the entry is already on screen with its date and the person it was with, and
 * what is being changed is the words. A dialog would take all of that away and
 * ask somebody to remember it.
 *
 * The subject is offered only where there is one. A note has no subject, and an
 * empty box labelled "Subject" invites somebody to invent one.
 */
function EditForm({
  entry,
  onSave,
  onCancel,
}: {
  entry: TimelineEntry;
  onSave: (patch: { subject?: string | null; body?: string | null }) => void;
  onCancel: () => void;
}) {
  const hasSubject = entry.kind !== "note";
  const [subject, setSubject] = useState(entry.subject ?? "");
  const [body, setBody] = useState(() => toText(entry.body ?? ""));

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          ...(hasSubject ? { subject: subject.trim() || null } : {}),
          body: body.trim() ? toHtml(body) : null,
        });
      }}
      className="mt-1 flex flex-col gap-1.5"
    >
      {hasSubject ? (
        <input
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          aria-label="Subject"
          className="w-full rounded-md border border-line bg-app px-2 py-1 text-sm text-content outline-none focus:border-brand-400"
        />
      ) : null}

      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={3}
        autoFocus
        aria-label="What happened"
        className="w-full rounded-md border border-line bg-app px-2 py-1.5 text-sm text-content outline-none focus:border-brand-400"
      />

      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="rounded bg-brand-600 px-2 py-0.5 text-[11px] text-white hover:bg-brand-700"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Cancel
        </button>
        <span className="text-[11px] text-faint">
          The row will say it was edited.
        </span>
      </div>
    </form>
  );
}
